import {
  createTheme as createV4Theme,
  Theme as V4Theme,
} from "@material-ui/core/styles";
import {
  createTheme as createV5Theme,
  Theme as V5Theme,
} from "@mui/material/styles";
import { DesignTokens, tokens as designTokens } from "./tokens";
import { dialogOverridesV4, dialogOverridesV5 } from "./dialogShell";

export type Themes = {
  v4: V4Theme;
  v5: V5Theme;
};

function requireToken(value: unknown, path: string): string {
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(`Missing required design token: ${path}`);
  }
  return value;
}

/**
 * Both libraries read the same role for the same job, so the mapping is written
 * once. Every line here is the guide's decision, not this file's:
 *
 *   primary.main   what a control uses when it carries a label, so it is
 *                  brand.primaryAction. Not brand.primary — white on that reads
 *                  3.43:1 and fails AA as body text, which is the entire reason
 *                  brand.600 exists (guide §2.2).
 *   primary.dark   what both libraries darken to on hover, and the guide names
 *                  that role: brand.primaryActionHover (guide §7).
 *   background     the page is the sunken surface; anything above it is the
 *                  default one (guide §2.4).
 *   divider        the quietest line that still separates (guide §2.4).
 */
function paletteFrom(tokens: DesignTokens) {
  const color = tokens?.color;
  return {
    primary: {
      main: requireToken(
        color?.brand?.primaryAction,
        "color.brand.primaryAction"
      ),
      dark: requireToken(
        color?.brand?.primaryActionHover,
        "color.brand.primaryActionHover"
      ),
      contrastText: requireToken(
        color?.brand?.onPrimary,
        "color.brand.onPrimary"
      ),
    },
    error: {
      main: requireToken(color?.status?.error, "color.status.error"),
      contrastText: requireToken(
        color?.brand?.onPrimary,
        "color.brand.onPrimary"
      ),
    },
    success: {
      main: requireToken(color?.status?.success, "color.status.success"),
      contrastText: requireToken(
        color?.brand?.onPrimary,
        "color.brand.onPrimary"
      ),
    },
    text: {
      primary: requireToken(color?.text?.primary, "color.text.primary"),
      secondary: requireToken(color?.text?.secondary, "color.text.secondary"),
      disabled: requireToken(color?.text?.disabled, "color.text.disabled"),
    },
    background: {
      default: requireToken(color?.surface?.sunken, "color.surface.sunken"),
      paper: requireToken(color?.surface?.default, "color.surface.default"),
    },
    divider: requireToken(color?.border?.default, "color.border.default"),
  };
}

/**
 * One family, everywhere (guide §3), and the heading scale the guide defines
 * (§2.9) wired to MUI's variants so every Typography heading is token-governed
 * instead of MUI's marketing defaults. h1..h6 map onto the guide's roles by
 * rank: display, heading, subheading, then the strong body/dense roles.
 */
function variantFrom(token: unknown, path: string) {
  const t = token as { size?: unknown; weight?: unknown; lineHeight?: unknown };
  if (!t || typeof t.size !== "string") {
    throw new Error(`Missing required design token: type.${path}`);
  }
  return { fontSize: t.size, fontWeight: t.weight, lineHeight: t.lineHeight };
}

function typographyFrom(tokens: DesignTokens) {
  const type = tokens?.type;
  return {
    fontFamily: requireToken(type?.family, "type.family"),
    h1: variantFrom(type?.display, "display"),
    h2: variantFrom(type?.heading, "heading"),
    h3: variantFrom(type?.subheading, "subheading"),
    h4: variantFrom(type?.bodyStrong, "bodyStrong"),
    h5: variantFrom(type?.denseStrong, "denseStrong"),
    h6: variantFrom(type?.denseStrong, "denseStrong"),
    body1: variantFrom(type?.body, "body"),
    body2: variantFrom(type?.dense, "dense"),
    caption: variantFrom(type?.caption, "caption"),
  };
}

export function buildV4Theme(tokens: DesignTokens): V4Theme {
  return createV4Theme({
    palette: paletteFrom(tokens),
    typography: typographyFrom(tokens),
    overrides: dialogOverridesV4(tokens),
    // Menus/popovers must not lock body scroll: doing so removes the page
    // scrollbar and shifts the width-driven sidebar when a dropdown opens.
    props: {
      MuiPopover: { disableScrollLock: true },
      MuiMenu: { disableScrollLock: true },
    },
  });
}

export function buildV5Theme(tokens: DesignTokens): V5Theme {
  return createV5Theme({
    palette: paletteFrom(tokens),
    typography: typographyFrom(tokens),
    components: {
      ...dialogOverridesV5(tokens),
      // See buildV4Theme: no scroll-lock on menus/popovers.
      MuiPopover: { defaultProps: { disableScrollLock: true } },
      MuiMenu: { defaultProps: { disableScrollLock: true } },
    },
  });
}

// Both themes are constructed before either is mounted: a failure in one must
// prevent a tree where only the other provider is present.
export function buildThemes(tokens: DesignTokens = designTokens): Themes {
  const v4 = buildV4Theme(tokens);
  const v5 = buildV5Theme(tokens);
  return { v4, v5 };
}
